import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot } from '@angular/router';
import { AuthService } from '../../services/auth';
import { MembershipService } from '../../services/membership/membership.service';
import { switchMap, map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

export const authGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const authService = inject(AuthService);
  const membershipService = inject(MembershipService);
  const router = inject(Router);

  const isAuthenticated = authService.isLoggedIn();
  if (!isAuthenticated) {
    router.navigate(['/auth/signin']);
    return false;
  }

  // Skip membership validation for system administrators
  if (authService.isSystemAdmin()) {
    return true;
  }

  // Skip membership check if we're already on the billing page to avoid infinite redirects
  const isBillingPage = route.url.some(segment => segment.path === 'billing') ||
                        route.url.join('/').includes('billing');
  if (isBillingPage) {
    return true;
  }

  // Check membership status from cache first
  const currentStatus = membershipService.getCurrentMembershipStatus();

  if (currentStatus) {
    // We have cached status, check if expired
    if (currentStatus.isExpired) {
      router.navigate(['/settings/billing'], { replaceUrl: true });
      return false;
    }
    return true;
  }

  // No cached status, fetch from server
  return membershipService.getMembershipStatus().pipe(
    map(status => {
      if (status.isExpired) {
        router.navigate(['/settings/billing'], { replaceUrl: true });
        return false;
      }
      return true;
    }),
    catchError(() => {
      // On error, assume expired and redirect to billing
      router.navigate(['/settings/billing'], { replaceUrl: true });
      return of(false);
    })
  );
};
